<template>
	<div class="d-flex flex-column cassie-vertical-md">
		<div
			class="d-flex cassie-horizontal-sm"
			:class="{ 'flex-wrap': isMultipleTextFields }"
		>
			<!-- Multiple text fields if columns are provided and isText is true -->
			<template v-if="isMultipleTextFields">
				<div
					v-for="(column, index) in columns"
					:key="index"
					class="flex-grow-1 pr-2 mb-2"
				>
					<TextField
						v-model="selection[column.key]"
						:label="column.label || `${entityType} ${column.key}`"
						:rules="textRules"
						:custom-messages="textCustomMessages"
						:test-id="testId ? `${testId}_${column.key}` : undefined"
						:disabled="disabled"
						:tooltip-text="column.tooltip"
					/>
				</div>
			</template>
			<!-- Single text field if isText is true and no columns are provided -->
			<TextField
				v-else-if="isText"
				v-model="selection"
				:label="`Add ${entityType}`"
				:rules="textRules"
				:custom-messages="textCustomMessages"
				style="flex: auto;"
				:test-id="testId"
				:disabled="disabled"
			/>

			<Dropdown
				v-else
				v-model="selection"
				:items="filteredOptions"
				:label="dropdownLabel"
				:rules="required ? { arrayIsNotEmpty: { value: selected, ignoreVModel: true } } : {}"
				:custom-messages="{ arrayIsNotEmpty: `You must add at least one ${entityType}` }"
				validation-mode="passive"
				style="flex: auto;"
				:test-id="testId"
				:searchable="searchable"
				:disabled="disabled"
			/>

			<PrimaryActionButton
				:disabled="disableAddButton"
				:test-id="testId ? `${testId}_add` : undefined"
				class="ml-2 align-self-end mb-2"
				@click="onAdd"
			>
				Add
			</PrimaryActionButton>
		</div>
		<v-card
			v-if="!selected.length"
			flat
			style="border: 1px solid currentColor !important; min-height: 100px;"
			class="background pa-4 d-flex align-center justify-center"
		>
			<span>
				No {{ entityType }}(s) have been added to this list yet
			</span>
		</v-card>
		<!-- Chips Layout -->
		<div
			v-else-if="displayAsChips"
			class="selected-chips d-flex flex-wrap"
		>
			<v-chip
				v-for="(item, index) in selected"
				:key="index"
				class="ma-1"
				close
				small
				:disabled="disabled"
				:data-testid="testId ? `${testId}_chip_${index}` : undefined"
				@click:close="onRemove(item)"
			>
				{{ getChipLabel(item) }}
			</v-chip>
		</div>
		<!-- Table Layout -->
		<v-simple-table
			v-else
			class="elevation-0 selected-table"
			dense
		>
			<thead>
				<tr>
					<template v-if="isMultipleTextFields">
						<th
							v-for="(col, cIdx) in columns"
							:key="cIdx"
						>
							{{ col.label || col.key }}
						</th>
					</template>
					<template v-else>
						<th>
							{{ entityType }}
						</th>
					</template>
					<th style="width: 56px;">
						Remove
					</th>
				</tr>
			</thead>
			<tbody>
				<tr
					v-for="(item, index) in selected"
					:key="index"
				>
					<template v-if="isMultipleTextFields">
						<td
							v-for="(col, cIdx) in columns"
							:key="cIdx"
						>
							{{ item[col.key] }}
						</td>
					</template>
					<template v-else>
						<td>
							{{ getChipLabel(item) }}
						</td>
					</template>
					<td class="text-center">
						<v-icon
							small
							:disabled="disabled"
							class="cursor-pointer"
							@click="onRemove(item)"
						>
							mdi-close
						</v-icon>
					</td>
				</tr>
			</tbody>
		</v-simple-table>
	</div>
</template>

<script>
import Dropdown from './dropdown.vue'
import PrimaryActionButton from './primary-action-button.vue'
import TextField from './text-field.vue'
export default {
	components: { TextField, PrimaryActionButton, Dropdown },
	props: {
		options: [Array, String],
		dropdownLabel: {
			type: String,
			default: 'Included'
		},
		entityType: {
			type: String,
			default: 'Item'
		},
		selected: {
			type: Array,
			default: () => []
		},
		required: Boolean,
		rules: Object,
		isText: Boolean,
		disabled: Boolean,
		testId: String,
		searchable: {
			type: Boolean,
			default: false
		},
		columns: {
			type: Array,
			default: () => []
		},
		displayAsChips: { // if true use chip layout, otherwise show table
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			selection: this.initializeSelection()
		}
	},
	computed: {
		filteredOptions () {
			return Array.isArray(this.options) ? this.options.filter(option => !this.selected.includes(option.value)) : []
		},
		textRules () {
			if (this.required) return { required: this.selected.length === 0 }
			else return this.rules || {}
		},
		textCustomMessages () {
			if (this.required) return { required: `You must add at least one ${this.entityType}` }
			else return {}
		},
		disableAddButton () {
			if (this.isMultipleTextFields) {
				return !this.selection ||
					Object.keys(this.selection).length === 0 ||
					Object.values(this.selection).some(val => !val) ||
					this.disabled
			}
			if (this.rules?.isUrlwithHttp) {
				return !this.selection ||
					this.disabled ||
					!(/^(http:\/\/|https:\/\/)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]/).test(this.selection)
			}
			return !this.selection || this.disabled
		},
		isMultipleTextFields () { return this.isText && this.columns && this.columns.length > 0 }
	},
	methods: {
		initializeSelection () {
			if (this.isText && this.columns && this.columns.length > 0) {
				return this.columns.reduce((obj, col) => {
					obj[col.key] = ''
					return obj
				}, {})
			}
			return null
		},
		onRemove (value) {
			this.updateSelected(this.selected.filter(s => s !== value))
		},
		onAdd () {
			const newItem = this.isMultipleTextFields
				? this.columns.reduce((obj, col) => {
					obj[col.key] = this.selection[col.key] || ''
					return obj
				}, {})
				: this.selection

			this.updateSelected([...this.selected, newItem])
			this.resetSelection()
		},
		resetSelection () {
			if (this.isMultipleTextFields) {
				this.selection = this.columns.reduce((obj, col) => {
					obj[col.key] = ''
					return obj
				}, {})
			} else {
				this.selection = null
			}
		},
		updateSelected (selected) {
			this.$emit('update:selected', selected)
		},
		getText (value) {
			if (this.isText) return value
			return this.options.find(option => option.value === value)?.text
		},
		getChipLabel (item) {
			if (typeof item === 'object' && item !== null) {
				// Concatenate object values defined by columns or all values
				if (this.columns && this.columns.length) {
					return this.columns.map(c => item[c.key]).filter(Boolean).join(' • ')
				}
				return Object.values(item).filter(Boolean).join(' • ')
			}
			return this.getText(item)
		}
		// textRules () {
		// 	if (this.required) return { required: this.selected.length === 0 }
		// 	else return this.rules || {}
		// },
	}
}
</script>

<style scoped>
.selected-chips { min-height: 48px; }
</style>
